// =============================================
// DASHBOARD DEL PROPIETARIO
// Vista principal del portal para owners
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  AccountBalance as AccountIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ownerPortalService from '../../services/ownerPortalService';

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await ownerPortalService.getDashboard();
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  };

  const getMonthName = (month) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1] || '-';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Bienvenida */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Bienvenido, {data.user.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Casa #{data.user.house_number}
        </Typography>
      </Box>

      {/* Cards de Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Cuota Mensual */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Cuota Mensual
                </Typography>
                <AccountIcon />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(data.summary.monthlyFee)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Mes Actual */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: data.summary.currentMonthPending > 0.01
              ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
              : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Mes Actual
                </Typography>
                {data.summary.currentMonthPending > 0.01 ? <WarningIcon /> : <CheckIcon />}
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(data.summary.currentMonthPaid)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {data.summary.currentMonthPending > 0.01 
                  ? `Pendiente: ${formatCurrency(data.summary.currentMonthPending)}`
                  : '¡Al día!'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Deuda Total */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: data.summary.totalDebt > 0
              ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
              : 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: data.summary.totalDebt > 0 ? 'white' : '#333'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Saldo Pendiente
                </Typography>
                <TrendingIcon />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(data.summary.totalDebt)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {data.summary.pendingMonthsCount} {data.summary.pendingMonthsCount === 1 ? 'mes' : 'meses'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Pagado Este Año */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Pagado Este Año
                </Typography>
                <PaymentIcon />
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatCurrency(data.summary.totalPaidThisYear)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Último Pago */}
      {data.lastPayment && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon /> Último Pago Registrado
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Fecha:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(data.lastPayment.payment_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Monto:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      {formatCurrency(data.lastPayment.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Período:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {getMonthName(data.lastPayment.month)} {data.lastPayment.year}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Acciones Rápidas */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Acciones Rápidas
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="contained" 
                  fullWidth
                  onClick={() => navigate('/owner/statement')}
                >
                  Ver Mi Estado de Cuenta
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => navigate('/owner/payments')}
                >
                  Ver Mis Pagos
                </Button>
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={() => navigate('/owner/expenses')}
                >
                  Ver Gastos de la Villa
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Meses Pendientes */}
      {data.pendingMonths && data.pendingMonths.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" /> Meses Pendientes de Pago
          </Typography>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Mes</TableCell>
                  <TableCell>Año</TableCell>
                  <TableCell align="right">Cuota</TableCell>
                  <TableCell align="right">Pagado</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pendingMonths.map((month, index) => (
                  <TableRow key={index}>
                    <TableCell>{getMonthName(month.month)}</TableCell>
                    <TableCell>{month.year}</TableCell>
                    <TableCell align="right">{formatCurrency(month.quota)}</TableCell>
                    <TableCell align="right">
                      <Typography color="success.main">
                        {formatCurrency(month.paid)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="error.main" fontWeight="bold">
                        {formatCurrency(month.pending)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell colSpan={4} align="right">
                    <Typography fontWeight="bold">TOTAL PENDIENTE:</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold" color="error.main" variant="h6">
                      {formatCurrency(data.summary.totalDebt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Mensaje de cuenta al día */}
      {(!data.pendingMonths || data.pendingMonths.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'success.light' }}>
          <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" color="success.main">
            ¡Cuenta al Día!
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            No tienes pagos pendientes. Gracias por tu puntualidad.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default OwnerDashboard;
